import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  Injectable,
  OnDestroy,
  ViewChild,
  ElementRef,
  AfterViewInit,
  inject,
  Renderer2,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { LoadScripts, DynamicScript } from 'src/app/shared/utilities/load-scripts.function';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { delay } from 'rxjs/operators';
import { WorksheetService } from '@fgb/core';
declare var YT: any;
declare var gtag: any;

@Injectable({ providedIn: 'root' })
export class YoutubeService {
  static behaviourSubject: BehaviorSubject<any>;

  constructor() {}

  addVideo(): Observable<any> {
    if (YoutubeService.behaviourSubject == null) {
      YoutubeService.behaviourSubject = new BehaviorSubject<any>('initializing');

      LoadScripts([{ src: 'https://www.youtube.com/iframe_api' }] as DynamicScript[]).then(() => {
        if (!(window as any)['onYouTubeIframeAPIReady']) {
          (window as any)['onYouTubeIframeAPIReady'] = (e: any) => {
            let status = YoutubeService.behaviourSubject.value;
            if (status != 'loaded') {
              YoutubeService.behaviourSubject.next('loaded');
            }
          };
        } else {
          let status = YoutubeService.behaviourSubject.value;
          if (status != 'loaded') {
            YoutubeService.behaviourSubject.next('loaded');
          }
        }
      });
    }

    return YoutubeService.behaviourSubject.asObservable();
  }
}

@Component({
  selector: 'fgb-youtube-player',
  templateUrl: './fgb-youtube-player.component.html',
  styleUrls: ['./fgb-youtube-player.component.scss'],
})
export class FgbYoutubePlayerComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  @Input() streamKey: string;
  @Input() autoplay: boolean = false;
  @Output() onStateChange = new EventEmitter();
  @ViewChild('playButton', { static: true }) private playButton: ElementRef;
  private subscription: Subscription;
  private eightyPercentWatched = false;
  public static analyticsInterval = 0;
  private renderer = inject(Renderer2);
  private ytPlayer: any;

  private progressCheckInterval: any;

  constructor(private youtubeService: YoutubeService, private worksheetService: WorksheetService) {}

  ngAfterViewInit(): void {
    this.subscription = this.youtubeService
      .addVideo()
      .pipe(delay(100))
      .subscribe((youtubeStatus) => {
        if (youtubeStatus == 'loaded') {
          this._initPlayer(this.streamKey);
        }
      });
  }
  ngOnInit() {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.streamKey && changes.streamKey.currentValue !== changes.streamKey.previousValue) {
      if (this.ytPlayer && YT.loaded) {
        this.playButton.nativeElement.style.display = 'none';
        this.ytPlayer.loadVideoById(this.streamKey);
      }
    }
  }

  private _initPlayer(key: string): void {
    this.ytPlayer = new YT.Player('ytplayer_' + key, {
      width: '100%',
      height: '100%',
      videoId: key,
      playerVars: {
        rel: 0,
        showinfo: 0,
        autoplay: this.autoplay ? 1 : 0,
        controls: 2,
        modestbranding: 1,
        iv_load_policy: 3,
      },
      events: {
        onReady: (event: any) => this.onPlayerReady(event),
        onStateChange: (event: any) => this.handleStateChange(event),
      },
    });
  }

  ngOnDestroy() {
    this.clearIntervals();
    this.subscription.unsubscribe();
  }

  pause(): void {
    if (this.ytPlayer && YT.loaded) {
      if (this.ytPlayer.getPlayerState() === YT.PlayerState.PLAYING) {
        this.ytPlayer.pauseVideo();
      }
    }
  }

  private onPlayerReady(event: any) {
    if (this.playButton) {
      this.renderer.listen(this.playButton.nativeElement, 'click', () => {
        if (event.target.getPlayerState() == YT.PlayerState.PLAYING) {
          event.target.pauseVideo();
        } else {
          this.playButton.nativeElement.style.display = 'none';
          event.target.playVideo();
        }
      });
    }
    if (this.autoplay) {
      this.playButton.nativeElement.style.display = 'none';
    }
  }

  private handleStateChange(event: any) {
    this.onStateChange.emit(event);

    if (event.data == YT.PlayerState.PLAYING) {
      // Video started playing
      this.startProgressTracking();
      this.startAnalyticsInterval();

      gtag('event', 'streaming', {
        streamKey: this.streamKey,
        event_category: 'start_stream',
        event_label: this.streamKey,
      });
    } else if (event.data == YT.PlayerState.PAUSED) {
      // Video paused
      gtag('event', 'streaming', {
        streamKey: this.streamKey,
        event_category: 'pause_stream',
        event_label: this.streamKey,
      });
      this.clearIntervals();
    } else if (event.data == YT.PlayerState.ENDED) {
      // Video ended
      this.clearIntervals();
    }
  }

  private startProgressTracking() {
    this.eightyPercentWatched = false;
    this.progressCheckInterval = setInterval(() => {
      try {
        const duration = this.ytPlayer.getDuration();
        const currentTime = this.ytPlayer.getCurrentTime();
        const progress = (currentTime / duration) * 100;

        if (progress >= 80 && !this.eightyPercentWatched) {
          this.eightyPercentWatched = true;
          this.handleEightyPercentWatched();
        }
      } catch (err) {
        this.clearIntervals();
      }
    }, 1000); // Check every second
  }

  private startAnalyticsInterval() {
    FgbYoutubePlayerComponent.analyticsInterval = window.setInterval(() => {
      gtag('event', 'streaming', {
        streamKey: this.streamKey,
        event_category: 'watching_stream',
        event_label: this.streamKey,
      });
    }, 30000);
  }

  private clearIntervals() {
    window.clearInterval(FgbYoutubePlayerComponent.analyticsInterval);
    window.clearInterval(this.progressCheckInterval);
  }

  private handleEightyPercentWatched() {
    // Sending the POST request
    this.worksheetService.logStreamWatching(this.streamKey);

    gtag('event', 'streaming', {
      streamKey: this.streamKey,
      event_category: '80_percent_watched',
      event_label: this.streamKey,
    });
  }
}
